import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Columns3, List, Plus, Search, X } from "lucide-react";
import { EmptyState, KindBadge, ProgressBar, StageChip } from "@/components/new-look/blocks";
import { STAGES, STAGE_ORDER, countOf, type Stage } from "@/components/new-look/labels";
import { NEEDS_YOU_TEXT, stageOf } from "@/components/new-look/stage";
import { useEpicRequests, useIdeaStageCounts } from "@/hooks/queries/use-epic-requests";
import { useTeams } from "@/hooks/queries/use-teams";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuthStore } from "@/stores/auth-store";
import type { EpicRequest, EpicRequestFilters, IdeaStageCounts } from "@/lib/api/epic-requests";
import { cn } from "@/lib/utils";
import { clarityTone, factsFor, progressOf, updatedAgo } from "./idea-helpers";
import { IdeasBoard } from "./ideas-board";

/**
 * All ideas (docs/new-look/PLAN.md, step 2.1): every idea in the company as a
 * list, searchable across titles, ids, descriptions, people and projects
 * (2.2), with each idea's real stage and build progress from the list API's
 * build facts (2.6), and stage chips with counts across all ideas (2.2b).
 * Saved views (2.3): All, Needs me, Building now, My ideas, Shipped.
 * Project filter and sort (2.4). A board with a column per stage (2.5).
 */

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

type ViewKey = "all" | "needs" | "building" | "mine" | "shipped";

/** Saved views (2.3): tabs over the list. "Me" is the signed-in person. */
const VIEWS: { key: ViewKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "needs", label: "Needs me" },
  { key: "building", label: "Building now" },
  { key: "mine", label: "My ideas" },
  { key: "shipped", label: "Shipped" },
];
const VIEW_STORAGE_KEY = "compbox-ideas-view";

/** The view a link asks for (?view=needs, from the Dashboard), else the last one used. */
function startingView(asked: string | null): ViewKey {
  return VIEWS.some((x) => x.key === asked) ? (asked as ViewKey) : storedView();
}

function storedView(): ViewKey {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return VIEWS.some((x) => x.key === v) ? (v as ViewKey) : "all";
  } catch {
    return "all";
  }
}

function rememberView(v: ViewKey): void {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, v);
  } catch {
    // Storage blocked: the view lasts until the page reloads.
  }
}

type Layout = "list" | "board";
const LAYOUT_STORAGE_KEY = "compbox-ideas-layout";

function storedLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_STORAGE_KEY) === "board" ? "board" : "list";
  } catch {
    return "list";
  }
}

function rememberLayout(v: Layout): void {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, v);
  } catch {
    // Storage blocked: the layout lasts until the page reloads.
  }
}

function LayoutSwitch({ value, onChange }: { value: Layout; onChange: (v: Layout) => void }) {
  const option = (v: Layout, label: string, Icon: typeof List) => (
    <button
      type="button"
      aria-pressed={value === v}
      onClick={() => {
        onChange(v);
      }}
      className={cn(
        "inline-flex min-h-11 items-center gap-1.5 px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        value === v
          ? "bg-accent text-accent-foreground"
          : "bg-background text-muted-foreground hover:text-foreground"
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {label}
    </button>
  );
  return (
    <div
      role="group"
      aria-label="Layout"
      className="flex shrink-0 overflow-hidden rounded-xl border border-border"
    >
      {option("list", "List", List)}
      {option("board", "Board", Columns3)}
    </div>
  );
}

/** What each view adds to the list's filters. Building now and Shipped fix the stage. */
export function viewFilters(view: ViewKey, me: string | undefined): EpicRequestFilters {
  switch (view) {
    case "needs":
      return { needsYou: true, ...(me ? { requestedById: me } : {}) };
    case "building":
      return { stages: "build" };
    case "mine":
      return me ? { requestedById: me } : {};
    case "shipped":
      return { stages: "done" };
    default:
      return {};
  }
}

function ViewTabs({
  value,
  counts,
  onChange,
}: {
  value: ViewKey;
  counts: Partial<Record<ViewKey, number | undefined>>;
  onChange: (v: ViewKey) => void;
}) {
  return (
    <nav
      aria-label="Views"
      className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0"
    >
      {VIEWS.map((v) => {
        const on = value === v.key;
        const count = counts[v.key];
        return (
          <button
            key={v.key}
            type="button"
            aria-current={on ? "page" : undefined}
            onClick={() => {
              onChange(v.key);
            }}
            className={cn(
              "-mb-px inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              on
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {v.label}
            {count !== undefined && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-px text-xs tabular-nums",
                  on ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                  v.key === "needs" &&
                    count > 0 &&
                    !on &&
                    "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200"
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

type StageChoice = "all" | "idea" | "blueprint" | "build" | "done" | "closed";

type SortKey = "updated" | "newest" | "title";

const SORTS: Record<
  SortKey,
  { label: string; filters: Pick<EpicRequestFilters, "orderBy" | "orderDirection"> }
> = {
  updated: { label: "Last updated", filters: { orderBy: "updatedAt", orderDirection: "desc" } },
  newest: { label: "Newest", filters: { orderBy: "createdAt", orderDirection: "desc" } },
  title: { label: "Title (A–Z)", filters: { orderBy: "title", orderDirection: "asc" } },
};

const SELECT =
  "min-h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Filters({
  project,
  onProject,
  sort,
  onSort,
}: {
  project: string;
  onProject: (v: string) => void;
  sort: SortKey;
  onSort: (v: SortKey) => void;
}) {
  const { data: teams } = useTeams();
  const projects = [...(teams?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted-foreground sm:flex-none">
        Project
        <select
          value={project}
          onChange={(e) => {
            onProject(e.target.value);
          }}
          className={cn(SELECT, "min-w-0 flex-1 sm:w-56 sm:flex-none")}
        >
          <option value="">All projects</option>
          {projects.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted-foreground sm:flex-none">
        Sort
        <select
          value={sort}
          onChange={(e) => {
            onSort(e.target.value as SortKey);
          }}
          className={cn(SELECT, "min-w-0 flex-1 sm:w-44 sm:flex-none")}
        >
          {(Object.keys(SORTS) as SortKey[]).map((k) => (
            <option key={k} value={k}>
              {SORTS[k].label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

const STAGE_CHOICES: { key: StageChoice; label: string; dot?: string }[] = [
  { key: "all", label: "All stages" },
  { key: "idea", label: STAGES.idea, dot: "bg-violet-600 dark:bg-violet-400" },
  { key: "blueprint", label: STAGES.blueprint, dot: "bg-cyan-600 dark:bg-cyan-400" },
  { key: "build", label: STAGES.build, dot: "bg-blue-600 dark:bg-blue-400" },
  { key: "done", label: STAGES.done, dot: "bg-green-600 dark:bg-green-400" },
  { key: "closed", label: "Rejected", dot: "bg-muted-foreground" },
];

function StageChips({
  value,
  counts,
  onChange,
}: {
  value: StageChoice;
  counts: IdeaStageCounts | undefined;
  onChange: (v: StageChoice) => void;
}) {
  return (
    <div role="group" aria-label="Stage" className="flex flex-wrap items-center gap-2">
      {STAGE_CHOICES.map((c) => {
        const on = value === c.key;
        const count = counts ? (c.key === "all" ? counts.total : counts[c.key]) : undefined;
        return (
          <button
            key={c.key}
            type="button"
            aria-pressed={on}
            onClick={() => {
              onChange(c.key);
            }}
            className={cn(
              "inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              on
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-background text-foreground/80 hover:bg-muted"
            )}
          >
            {c.dot && <span aria-hidden="true" className={cn("h-2 w-2 rounded-full", c.dot)} />}
            {c.label}
            {count !== undefined && (
              <span className={cn("tabular-nums", on ? "opacity-80" : "text-muted-foreground")}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex min-h-11 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 shadow-[0_2px_8px_rgba(37,99,235,0.06)] focus-within:ring-2 focus-within:ring-ring">
      <Search className="h-[18px] w-[18px] shrink-0 text-muted-foreground" aria-hidden="true" />
      <label htmlFor="ideas-search" className="sr-only">
        Search ideas
      </label>
      <input
        id="ideas-search"
        type="search"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder="Search titles, people, projects…"
        className="min-w-0 flex-1 border-0 bg-transparent text-[15px] text-foreground outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange("");
          }}
          aria-label="Clear search"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
const GRID =
  "grid grid-cols-[minmax(0,1fr)_auto] gap-3 md:grid-cols-[minmax(0,2.6fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.7fr)_minmax(0,1fr)_minmax(0,0.9fr)]";

function IdeaRow({ idea }: { idea: EpicRequest }) {
  const { stage, closed, needsYou } = stageOf(factsFor(idea));
  const progress = closed ? null : progressOf(idea, stage);
  const clarity = idea.complianceScore?.overall;
  const who = idea.requestedBy?.name;

  return (
    <li>
      <Link
        to={`/ideas/${idea.id}`}
        className={cn(
          GRID,
          "items-center border-b border-border px-4 py-3.5 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none md:px-5"
        )}
      >
        <span className="flex min-w-0 items-start gap-3">
          <KindBadge kind="software" />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate text-[15px] font-semibold text-foreground">
                {idea.title}
              </span>
              {needsYou && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                  {NEEDS_YOU_TEXT[needsYou]}
                </span>
              )}
            </span>
            <span className="truncate text-[13px] text-muted-foreground">
              {[idea.identifier, who].filter(Boolean).join(" · ")}
            </span>
            {progress !== null && (
              <span className="mt-1 flex items-center gap-2 md:hidden">
                <ProgressBar
                  value={progress}
                  label={`${idea.title} progress`}
                  stage={stage}
                  className="flex-1"
                />
                <span className="text-xs tabular-nums text-muted-foreground">{progress}%</span>
              </span>
            )}
          </span>
        </span>
        <span className="hidden truncate text-sm text-foreground/80 md:block">
          {idea.team?.name ?? "Personal"}
        </span>
        {closed ? (
          <span className="inline-flex min-h-6 items-center self-center justify-self-start rounded-full bg-muted px-2.5 text-xs font-semibold text-muted-foreground">
            Rejected
          </span>
        ) : (
          <StageChip stage={stage} className="self-center justify-self-start" />
        )}
        <span
          className={cn(
            "hidden text-sm font-semibold tabular-nums md:block",
            clarity === undefined ? "text-muted-foreground" : clarityTone(clarity)
          )}
        >
          {clarity ?? "—"}
        </span>
        {progress === null ? (
          <span className="hidden text-sm text-muted-foreground md:block">—</span>
        ) : (
          <span className="hidden items-center gap-2 md:flex">
            <ProgressBar
              value={progress}
              label={`${idea.title} progress`}
              stage={stage}
              className="flex-1"
            />
            <span className="w-9 text-right text-xs tabular-nums text-muted-foreground">
              {progress}%
            </span>
          </span>
        )}
        <span className="hidden text-[13px] text-muted-foreground md:block">
          {updatedAgo(idea.updatedAt)}
        </span>
      </Link>
    </li>
  );
}

export function AllIdeasPage() {
  usePageTitle("Ideas");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  const [stage, setStage] = useState<StageChoice>("all");
  const [params] = useSearchParams();
  const [view, setView] = useState<ViewKey>(() => startingView(params.get("view")));
  const [project, setProject] = useState("");
  const [sort, setSort] = useState<SortKey>("updated");
  const [layout, setLayout] = useState<Layout>(storedLayout);
  const chooseLayout = (v: Layout) => {
    setLayout(v);
    rememberLayout(v);
  };
  const me = useAuthStore((state) => state.user?.id);
  const stageFixed = view === "building" || view === "shipped";

  const chooseView = (v: ViewKey) => {
    setView(v);
    setStage("all");
    rememberView(v);
  };

  const filters: EpicRequestFilters = {
    limit: PAGE_SIZE,
    withFacts: true,
    ...SORTS[sort].filters,
    ...(search ? { search } : {}),
    ...(project ? { teamId: project } : {}),
    ...(stage === "all" || stageFixed ? {} : { stages: stage }),
    ...viewFilters(view, me),
  };
  const searchFilter = { ...(search ? { search } : {}), ...(project ? { teamId: project } : {}) };
  const { data: allCounts } = useIdeaStageCounts(searchFilter);
  const { data: myCounts } = useIdeaStageCounts(
    me ? { ...searchFilter, requestedById: me } : searchFilter
  );
  // The chips count within the view: everyone's ideas, or just mine.
  const counts = view === "needs" || view === "mine" ? myCounts : allCounts;
  const viewCounts: Partial<Record<ViewKey, number | undefined>> = {
    all: allCounts?.total,
    needs: myCounts?.needsYou,
    building: allCounts?.build,
    mine: myCounts?.total,
    shipped: allCounts?.done,
  };
  const onBoard = layout === "board";
  const { stages: _stage, limit: _limit, ...boardFilters } = filters;
  const boardStages: readonly Stage[] =
    view === "building" ? ["build"] : view === "shipped" ? ["done"] : STAGE_ORDER;
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useEpicRequests(filters, { enabled: !onBoard });
  const ideas = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.meta.totalCount;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">All ideas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every idea in your company, from first line to shipped.
            {total !== undefined && ` ${countOf(total, "idea")}.`}
          </p>
        </div>
        <Link
          to="/ideas/new"
          className="inline-flex min-h-11 items-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          New idea
        </Link>
      </div>

      <ViewTabs value={view} counts={viewCounts} onChange={chooseView} />
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <SearchBox value={query} onChange={setQuery} />
        <Filters project={project} onProject={setProject} sort={sort} onSort={setSort} />
        <LayoutSwitch value={layout} onChange={chooseLayout} />
      </div>
      {!stageFixed && !onBoard && <StageChips value={stage} counts={counts} onChange={setStage} />}

      {onBoard ? (
        <IdeasBoard filters={boardFilters} stages={boardStages} />
      ) : isLoading ? (
        <div aria-busy="true" aria-label="Loading ideas" className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          title="Couldn't load ideas"
          body="Check your connection and refresh the page."
        />
      ) : ideas.length === 0 && view === "needs" && !search && stage === "all" && !project ? (
        <EmptyState
          title="Nothing needs you"
          body="No questions, merges or approvals are waiting on your ideas."
        />
      ) : ideas.length === 0 && (search || stage !== "all" || view !== "all" || project) ? (
        <EmptyState
          title="No ideas match"
          body={
            search
              ? `Nothing matches “${search}” here. Try other words or another stage.`
              : "No ideas are at this stage right now."
          }
          action={
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setProject("");
                chooseView("all");
              }}
              className="min-h-11 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
            >
              Show all ideas
            </button>
          }
        />
      ) : ideas.length === 0 ? (
        <EmptyState
          title="No ideas yet"
          body="Say it in a line. CompBox interviews you until the idea is clear, then makes the blueprint and builds it."
        />
      ) : (
        <section aria-label="Ideas" className="overflow-hidden rounded-2xl border border-border">
          <div
            aria-hidden="true"
            className={cn(
              GRID,
              "hidden border-b border-border bg-muted/50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:grid"
            )}
          >
            <span>Idea</span>
            <span>Project</span>
            <span>Stage</span>
            <span>Clarity</span>
            <span>Progress</span>
            <span>Updated</span>
          </div>
          <ul>
            {ideas.map((idea) => (
              <IdeaRow key={idea.id} idea={idea} />
            ))}
          </ul>
        </section>
      )}

      {!onBoard && hasNextPage && (
        <button
          type="button"
          disabled={isFetchingNextPage}
          onClick={() => {
            void fetchNextPage();
          }}
          className="min-h-11 self-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
        >
          {isFetchingNextPage ? "Loading…" : "Show more"}
        </button>
      )}
    </div>
  );
}
